import { idLigneDepuisEvenement } from '../../../../partage/table/ligne-table';
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe, PercentPipe } from '@angular/common';
import { MeterComponent } from '@shared/components/meter';
import { ModalService } from '@shared/components/modal';
import { PaginationControlComponent } from '@shared/components/pagination-control';
import { IconComponent } from '@shared/components/icon';
import { ContextMenuComponent } from '@shared/components/context-menu/context-menu.component';
import { TableComponent } from '@shared/components/table';
import { toSignal } from '@angular/core/rxjs-interop';
import { formaterMontant } from '../../../../partage/format/montant';
import { EllipsisTooltipDirective } from '@shared/directives/tooltip';
import { EnteteTriableComponent } from '../../../../partage/table/entete-triable.component';
import { EnteteInfoComponent } from '../../../../partage/table/entete-info.component';
import { TaillePageComponent } from '../../../../partage/table/taille-page.component';

import type { IntentionTri, SensTri } from '../../../../partage/table/entete-triable.component';

import type { TemplateRef } from '@angular/core';
import type { TableColumn } from '@shared/components/table';
import type { MenuAction } from '@shared/components/action-menu/action-menu.component';
import type { StatusTone } from '@shared/components/status-chip';
import type { RequestStatus } from '@shared/stores/features';
import { Component, computed, inject, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FactureService } from '../../../facturation/facture.service';
import { AcompteAnnulationService } from '../../evenements/dialog-acompte-annulation/acompte-annulation.service';
import { RedditionAnnulationService } from '../../evenements/dialog-reddition-annulation/reddition-annulation-service';
import { AnnulationAcompteData } from '../../global/acompte-annulation.interface';
import { RapprochementStatutClassPipe } from '../../global/pipe/rapprochement-statut-class.pipe';
import { DialogAcompteAnnulationComponent } from '../dialog-acompte-annulation/dialog-acompte-annulation.component';
import { DialogAcompteRedditionValidationComponent } from '../dialog-acompte-reddition-validation/dialog-acompte-reddition-validation.component';
import {
  DialogDataInput,
  DialogReturn,
  TypeDialog,
} from '../dialog-acompte-reddition-validation/dialog-acompte-reddition.interface';
import { DialogRedditionAnnulationComponent } from '../dialog-reddition-annulation/dialog-reddition-annulation.component';
import { AnnulationRedditionData } from '../dialog-reddition-annulation/dialog-reddition-annulation.interface';
import { DialogGestionManuelleComponent } from '../../global/dialog-gestion-manuelle/dialog-gestion-manuelle.component';
import { GestionManuelleDialogData, GestionManuelleResult } from '../../global/gestion-manuelle.interface';
import { FeatureFlagService } from '../../../../partage/feature-flag/feature-flag.service';
import { AnnulationDialogDataInput, Formule, StatutFormule } from '../formules.interface';
import { FormulesService } from '../formules.service';
import { FormuleStatutClassPipe } from '../pipe/formule-statut-class.pipe';
import { FormuleStatutPipe } from '../pipe/formule-statut.pipe';
import { MontantPipe } from '../../../../partage/pipes/montant.pipe';

//traduction pour pagination
@Component({
  selector: 'app-formules-liste',
  imports: [
    MontantPipe,
    PercentPipe,
    DatePipe,
    RouterModule,
    MeterComponent,
    PaginationControlComponent,
    TableComponent,
    ContextMenuComponent,
    IconComponent,
    EllipsisTooltipDirective,
    EnteteTriableComponent,
    EnteteInfoComponent,
    TaillePageComponent,
  ],
  templateUrl: './formules-liste.component.html',
  styleUrl: './formules-liste.component.scss',
  providers: [
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'dd/MM/yyyy HH:mm:ss', timezone: '+0000' },
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormulesListeComponent {
  private formuleService = inject(FormulesService);
  private acompteAnnulationService = inject(AcompteAnnulationService);
  private redditionAnnulationService = inject(RedditionAnnulationService);
  private factureService = inject(FactureService);

  formules = this.formuleService.formules;
  sortFilter = this.formuleService.sortFilter;
  pageFilter = this.formuleService.pageFilter;
  voirDetail = this.formuleService.voirDetail;
  panelFormule = this.formuleService.formuleSelection;
  voirPDF = this.factureService.voirPdf;
  private modal = inject(ModalService);

  nomColumns: string[] = [
    'nom',
    'idAparte',
    'date',
    'nomClient',
    'statutRapprochementOk',
    'montantTotal',
    'totalReverse',
    'montantDu',
    'statutFormule',
  ];
  nomColumnsAvecDetail = [...this.nomColumns, 'slider'];

  // ── Ponts vers app-table ─────────────────────────────────────────────────
  private readonly gabaritDate = viewChild<TemplateRef<{ $implicit: Formule }>>('gabaritDate');
  private readonly gabaritRapprochement = viewChild<TemplateRef<{ $implicit: Formule }>>('gabaritRapprochement');
  private readonly gabaritTotalReverse = viewChild<TemplateRef<{ $implicit: Formule }>>('gabaritTotalReverse');

  /* En-tetes passes en `headerTemplate`. Contexte-libre par conception du kit :
     un gabarit par colonne. */
  private readonly enteteNom = viewChild<TemplateRef<unknown>>('enteteNom');
  private readonly enteteIdAparte = viewChild<TemplateRef<unknown>>('enteteIdAparte');
  private readonly enteteDate = viewChild<TemplateRef<unknown>>('enteteDate');
  private readonly enteteNomClient = viewChild<TemplateRef<unknown>>('enteteNomClient');
  private readonly enteteMontantTotal = viewChild<TemplateRef<unknown>>('enteteMontantTotal');
  private readonly enteteTotalReverse = viewChild<TemplateRef<unknown>>('enteteTotalReverse');

  /** Champ et sens du tri courant, lus du filtre envoye au serveur. */
  protected readonly champTri = computed<string>(() => this.sortFilter().orderingField);
  protected readonly sensTri = computed<SensTri>(() => (this.sortFilter().orderingDirection === 1 ? 'desc' : 'asc'));

  /**
   * Applique une intention de tri emise par un en-tete.
   *
   * Retour a la premiere page : changer l'ordre rebat toutes les lignes, rester
   * page 5 afficherait une tranche arbitraire. Les deux signaux sont poses dans le
   * meme tick, donc une seule requete part.
   */
  protected trier(intention: IntentionTri): void {
    this.formuleService.trier(intention.champ, intention.sens);
  }

  /**
   * Change le nombre de lignes par page.
   *
   * Remise a zero de l'index OBLIGATOIRE : rester page 8 en passant de 25 a 100
   * lignes demande une page qui n'existe plus, et la liste revient vide.
   */
  protected changerTaillePage(taille: number): void {
    this.formuleService.changerTaillePage(taille);
  }

  protected readonly identifiantLigne = (row: Formule): string => String(row.id);

  /* Etat rendu par le squelette d'`app-table`. Il etait fige sur `fulfilled` :
     aucun chargement n'etait donc jamais visible. */
  protected statutChargement = computed<RequestStatus>(() =>
    this.formuleService.chargementListe() ? 'pending' : 'fulfilled',
  );

  /* Le tri d'en-tete n'existe pas dans le kit V3 (« reserved for the deferred
     header-sort seam »). L'ordre serveur reste fixe sur la date. */

  protected pourcentageReverse(row: Formule): number {
    const total = row.totalReverse + row.montantDu;
    return total === 0 ? 0 : (row.totalReverse / total) * 100;
  }

  protected pageIndexAffiche = computed(() => {
    const filtre = this.pageFilter();
    return filtre.numeroPage * filtre.nbrParPage > this.formules().totalCount ? 0 : filtre.numeroPage;
  });

  protected nombrePages = computed(() =>
    Math.max(1, Math.ceil(this.formules().totalCount / this.pageFilter().nbrParPage)),
  );

  protected changerPage(page: number): void {
    this.formuleService.changerPage(page);
  }

  protected colonnes = computed<readonly TableColumn<Formule>[]>(() => {
    const date = this.gabaritDate();
    const rapprochement = this.gabaritRapprochement();
    const totalReverse = this.gabaritTotalReverse();
    if (!date || !totalReverse || !rapprochement) return [];

    return [
      /* `headerHidden` + `headerTemplate` : `header` reste le libelle ACCESSIBLE,
         le gabarit porte le bouton de tri. Les 4 colonnes triables sont celles qui
         l'etaient avant la migration — l'API n'accepte pas d'autre champ. */
      {
        id: 'nom',
        header: 'Evènement',
        headerHidden: true,
        headerTemplate: this.enteteNom(),
        kind: 'text',
        value: row => row.nom,
      },
      {
        id: 'idAparte',
        header: 'Id session',
        headerHidden: true,
        headerTemplate: this.enteteIdAparte(),
        kind: 'text',
        value: row => String(row.idAparte),
      },
      {
        id: 'date',
        header: 'Date & heure',
        headerHidden: true,
        headerTemplate: this.enteteDate(),
        kind: 'custom',
        template: date,
      },
      {
        id: 'nomClient',
        header: 'Client',
        headerHidden: true,
        headerTemplate: this.enteteNomClient(),
        kind: 'text',
        value: row => row.nomClient,
      },
      {
        id: 'statutRapprochementOk',
        header: 'Rapprochement',
        kind: 'custom',
        template: rapprochement,
      },
      /* `headerInfo` n'existe PAS dans `TableColumn` : ces deux infobulles etaient
         declarees mais jamais rendues. Elles passent par `headerTemplate`, rendu
         APRES le texte de l'en-tete — d'ou l'absence de `headerHidden` ici. */
      {
        id: 'montantTotal',
        header: 'Montant total',
        headerTemplate: this.enteteMontantTotal(),
        kind: 'text',
        align: 'end',
        value: row => formaterMontant(row.montantTotal),
      },
      {
        id: 'totalReverse',
        header: 'Total reversé',
        headerTemplate: this.enteteTotalReverse(),
        kind: 'custom',
        template: totalReverse,
      },
      { id: 'montantDu', header: 'Reste dû', kind: 'text', align: 'end', value: row => formaterMontant(row.montantDu) },
      {
        id: 'statutFormule',
        header: 'Statut',
        kind: 'status',
        value: row => ({
          tone: this.tonaliteStatut(row.statutFormule),
          label: this.libelleStatut.transform(row.statutFormule),
        }),
      },
      {
        id: 'slider',
        header: 'Actions',
        headerHidden: true,
        align: 'end',
        kind: 'actions',
        actions: row => this.actionsPour(row),
      },
    ];
  });

  /** Drapeau resolu en TypeScript : le menu se construit en amont du gabarit, la
   *  directive structurelle `appFeatureEnabled` n'a donc plus de prise. */
  private readonly gestionManuelleActive = toSignal(inject(FeatureFlagService).isEnabled('GestionManuelle'), {
    initialValue: false,
  });

  private readonly router = inject(Router);

  /**
   * Actions de la ligne, rendues par le menu « ⋯ » du design system au lieu d'une
   * pile de boutons dans la cellule. Conditions identiques aux anciens `@if`.
   */
  /* Les pipes d'origine restent la source de verite du libelle et de la nuance de
     statut : `app-status-chip` n'ayant pas d'entree icone, seule la colonne
     Rapprochement repasse par un gabarit. Les pipes sont appeles depuis le
     TypeScript, les colonnes s'y declarant. */
  private readonly libelleStatut = new FormuleStatutPipe();
  private readonly classeStatut = new FormuleStatutClassPipe();
  private readonly classeRapprochement = new RapprochementStatutClassPipe();

  /** `capsOk` / `capsKo` / ... des pipes -> tonalites du design system. */
  private static readonly TONALITES: Readonly<Record<string, StatusTone>> = {
    capsOk: 'success',
    capsKo: 'error',
    capsWait: 'warning',
    capsWarning: 'warning',
    capsInfo: 'info',
  };

  private tonaliteStatut(statut: Parameters<FormuleStatutClassPipe['transform']>[0]): StatusTone {
    return FormulesListeComponent.TONALITES[this.classeStatut.transform(statut)] ?? 'muted';
  }

  /**
   * Rapprochement : trois etats, pas deux. Le pipe distingue un retard de plus de
   * 3 jours (erreur) d'un retard recent (avertissement) ; ma premiere version ne
   * gardait que ok/ko et perdait cette nuance.
   */
  protected classesRapprochement(row: Formule): string {
    const tonalite =
      FormulesListeComponent.TONALITES[
        this.classeRapprochement.transform(row.statutRapprochementOk, row.datePlusVieuxReglementNonRapproche)
      ] ?? 'muted';
    return {
      success: 'bg-status-success-bg text-status-success',
      error: 'bg-status-error-bg text-status-error',
      warning: 'bg-status-warning-bg text-status-warning',
      info: 'bg-status-info-bg text-status-info',
      muted: 'bg-surface-tertiary text-content-tertiary',
    }[tonalite];
  }

  /* Menu contextuel : le clic droit sur une ligne ouvre les memes actions que
     le menu « ⋯ ». On retrouve la ligne via le `data-testid` que pose
     `app-table`, plutot que d'ajouter une sortie au kit. Le prefixe et son contrat
     vivent dans `partage/table/ligne-table.ts`, garde par une spec. */
  protected readonly positionMenu = signal<{ x: number; y: number } | null>(null);
  protected readonly actionsMenu = signal<readonly MenuAction[]>([]);
  private ligneMenu: Formule | null = null;

  protected ouvrirMenuContextuel(evenement: MouseEvent): void {
    const id = idLigneDepuisEvenement(evenement);
    const ligne = id ? this.formules().items.find(r => this.identifiantLigne(r) === id) : undefined;
    if (!ligne) return;

    evenement.preventDefault();
    this.ligneMenu = ligne;
    this.actionsMenu.set(this.actionsPour(ligne));
    this.positionMenu.set({ x: evenement.clientX, y: evenement.clientY });
  }

  protected fermerMenuContextuel(): void {
    this.positionMenu.set(null);
    this.ligneMenu = null;
  }

  protected executerActionContextuelle(id: string): void {
    const ligne = this.ligneMenu;
    this.fermerMenuContextuel();
    if (ligne) this.executerAction({ id, row: ligne });
  }

  protected actionsPour(row: Formule): readonly MenuAction[] {
    const actions: MenuAction[] = [{ kind: 'leaf' as const, id: 'detail', label: 'Voir les details', icon: 'search' }];

    if (this.gestionManuelleActive() && this.gestionManuellePossible(row.statutFormule)) {
      actions.push({ kind: 'leaf' as const, id: 'gestionManuelle', label: 'Gerer manuellement', icon: 'sliders' });
    }
    if (!row.statutRapprochementOk) {
      actions.push({
        kind: 'leaf' as const,
        id: 'rapprochements',
        label: 'Verifier les rapprochements',
        icon: 'layers',
      });
    }
    if (row.montantDu > 0 && this.acomptePossible(row.acompteEnCours, row.statutFormule)) {
      actions.push({ kind: 'leaf' as const, id: 'acompte', label: "Faire une demande d'acompte", icon: 'euro' });
    }
    if (this.annulationAcomptePossible(row.acompteEnCours, row.statutFormule)) {
      actions.push({
        kind: 'leaf' as const,
        id: 'annulerAcompte',
        label: "Annuler l'acompte en cours",
        destructive: true,
      });
    }
    if (this.annulationRedditionPossible(row.statutFormule)) {
      actions.push({
        kind: 'leaf' as const,
        id: 'annulerReddition',
        label: 'Annuler la reddition en cours',
        destructive: true,
      });
    }
    if (row.statutRapprochementOk && this.formuleARedditionner(row.statutFormule)) {
      actions.push({ kind: 'leaf' as const, id: 'redditionner', label: 'Redditionner la formule', icon: 'check' });
    }
    return actions;
  }

  protected executerAction({ id, row }: { id: string; row: Formule }): void {
    switch (id) {
      case 'detail':
        this.afficherDetailFormule(row);
        break;
      case 'gestionManuelle':
        this.gererManuellement(row);
        break;
      case 'rapprochements':
        // Etait un <a routerLink> dans le gabarit : la navigation devient programmatique.
        void this.router.navigate(['/rapprochement/rapprochement-manuel'], {
          queryParams: { codeClient: row.nomClient },
        });
        break;
      case 'acompte':
        this.demanderAcompte(row);
        break;
      case 'annulerAcompte':
        this.annulerAcompteEnCours(row);
        break;
      case 'annulerReddition':
        this.annulerRedditionEnCours(row);
        break;
      case 'redditionner':
        this.demanderReddition(row);
        break;
    }
  }

  constructor() {
    /* Tri par defaut a l'arrivee sur l'ecran : date croissante, comme
       `matSortActive="date" matSortDirection="asc"` avant la migration. */
    this.sortFilter.set({ orderingField: 'date', orderingDirection: 0 });
  }

  testIsOverflow(el: HTMLElement): boolean {
    const curOverflow = el.style.overflow;
    if (!curOverflow || curOverflow === 'visible') {
      el.style.overflow = 'hidden';
    }
    const isOverflow = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
    el.style.overflow = curOverflow;
    return isOverflow;
  }

  afficherDetailFormule(formule: Formule) {
    this.panelFormule.set(formule);
    this.voirDetail.set(true);
  }
  acomptePossible(acompteEnCours: boolean, formuleStatut: StatutFormule): boolean {
    return (
      (formuleStatut === StatutFormule.Acompte ||
        formuleStatut === StatutFormule.EnCours ||
        formuleStatut === StatutFormule.AControler) &&
      !acompteEnCours
    );
  }

  demanderAcompte(formule: Formule) {
    const dialogData: DialogDataInput = {
      formule: formule,
      type: TypeDialog.acompte,
    };
    this.openDialogAcompteReddition(dialogData);
  }

  demanderReddition(formule: Formule) {
    const dialogData: DialogDataInput = {
      formule: formule,
      type: TypeDialog.reddition,
    };
    this.openDialogAcompteReddition(dialogData);
  }

  openDialogAcompteReddition(dialogData: DialogDataInput) {
    this.modal
      .openForm<DialogReturn, DialogDataInput>(DialogAcompteRedditionValidationComponent, { data: dialogData })
      .closed.subscribe(result => {
        // `DialogRef.closed` émet `undefined` si la modale se ferme sans résultat
        if (result?.valide) {
          if (dialogData.type === TypeDialog.acompte) {
            this.formuleService.acompteACreer.set({
              idFormule: dialogData.formule.id,
              idCompteBancaire: result.idCompteBancaire,
              montantAcompte: result.montantAcompte,
            });
          } else if (dialogData.type === TypeDialog.reddition) {
            this.formuleService.redditionACreer.set({
              idFormule: dialogData.formule.id,
              idCompteBancaire: result.idCompteBancaire,
            });
          }
        }
      });
  }

  annulationRedditionPossible(formuleStatut: StatutFormule): boolean {
    return formuleStatut === StatutFormule.Redditionne;
  }

  annulationAcomptePossible(acompteEnCours: boolean, formuleStatut: StatutFormule): boolean {
    return acompteEnCours && formuleStatut !== StatutFormule.Redditionne && formuleStatut !== StatutFormule.Reverse;
  }

  annulerAcompteEnCours(formule: Formule) {
    const dialogData: AnnulationDialogDataInput = {
      formule: formule,
    };
    this.openDialogAnnulationAcompte(dialogData);
  }

  annulerRedditionEnCours(formule: Formule) {
    const dialogData: AnnulationDialogDataInput = {
      formule: formule,
    };
    this.openDialogAnnulationReddition(dialogData);
  }

  formuleARedditionner(formuleStatut: StatutFormule): boolean {
    return formuleStatut === StatutFormule.ARedditionner;
  }

  gestionManuellePossible(formuleStatut: StatutFormule): boolean {
    return (
      formuleStatut !== StatutFormule.GereManuellement &&
      formuleStatut !== StatutFormule.Reverse &&
      formuleStatut !== StatutFormule.Redditionne
    );
  }

  gererManuellement(formule: Formule): void {
    const dialogData: GestionManuelleDialogData = {
      nom: formule.nom,
      idAparte: formule.idAparte,
    };
    this.modal
      .openForm<GestionManuelleResult | undefined, GestionManuelleDialogData>(DialogGestionManuelleComponent, {
        data: dialogData,
      })
      .closed.subscribe(result => {
        if (result) {
          this.formuleService.gestionManuelleACreer.set({
            idFormule: formule.id,
            commentaire: result.commentaire,
          });
        }
      });
  }

  openDialogAnnulationAcompte(dialogData: AnnulationDialogDataInput) {
    this.modal
      .openForm<AnnulationAcompteData, AnnulationDialogDataInput>(DialogAcompteAnnulationComponent, {
        data: dialogData,
      })
      .closed.subscribe(result => {
        if (result) {
          this.acompteAnnulationService.acompteAAnnuler.set(result);
        }
      });
  }

  openDialogAnnulationReddition(dialogData: AnnulationDialogDataInput) {
    this.modal
      .openForm<AnnulationRedditionData, AnnulationDialogDataInput>(DialogRedditionAnnulationComponent, {
        data: dialogData,
      })
      .closed.subscribe(result => {
        if (result) {
          this.redditionAnnulationService.redditionAAnnuler.set(result);
        }
      });
  }
}
